import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axiosInstance from '../../utils/axiosConfig';

/**
 * Dropdown de busqueda global cross-proyecto. Se monta debajo del input del
 * shell y consulta /api/proyectos/buscar?q=<query> con debounce 200ms.
 *
 * Props:
 *   query              - texto actual del input (controlled por el padre)
 *   onClose            - cerrar dropdown sin limpiar input (Esc, click afuera)
 *   onResultSelected   - tras seleccionar+navegar; padre tipicamente cierra + limpia
 *
 * Keyboard:
 *   ArrowUp/Down  - mover seleccion
 *   Enter         - abrir el item resaltado
 *   Esc           - cerrar (delegado al padre via onClose)
 */
const GlobalSearchDropdown = ({ query, onClose, onResultSelected }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ proyectos: [], tareas: [] });
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);

  // Flatten para keyboard nav cross-secciones.
  const items = useMemo(
    () => [
      ...data.proyectos.map((p) => ({ type: 'proyecto', payload: p })),
      ...data.tareas.map((t) => ({ type: 'tarea', payload: t })),
    ],
    [data]
  );

  // Fetch con debounce 200ms + flag de race.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setData({ proyectos: [], tareas: [] });
      setLoading(false);
      return;
    }
    let ignored = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const { data: res } = await axiosInstance.get('/api/proyectos/buscar', {
          params: { q },
        });
        if (!ignored) {
          setData(res || { proyectos: [], tareas: [] });
          setActiveIndex(0);
        }
      } catch (err) {
        if (!ignored) console.error('Error buscar:', err);
      } finally {
        if (!ignored) setLoading(false);
      }
    }, 200);
    return () => {
      ignored = true;
      clearTimeout(handle);
    };
  }, [query]);

  const handleSelect = (item) => {
    if (item.type === 'proyecto') {
      navigate(`/proyectos/${item.payload.slug}`);
    } else {
      navigate(`/proyectos/${item.payload.proyecto.slug}/tareas/${item.payload.numero}`);
    }
    onResultSelected?.();
  };

  // Keyboard global mientras el dropdown esta montado. Intercepta solo flechas
  // y Enter cuando hay items; cualquier otra tecla pasa al input normal.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        if (items.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        if (items.length === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === 'Enter') {
        if (items.length === 0) return;
        e.preventDefault();
        const item = items[activeIndex];
        if (item) handleSelect(item);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeIndex]);

  // Scroll del item activo al viewport del dropdown.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const renderProyectoRow = (p, idx) => {
    const active = idx === activeIndex;
    return (
      <button
        key={`p-${p.id}`}
        data-idx={idx}
        type="button"
        onMouseDown={(e) => { e.preventDefault(); handleSelect({ type: 'proyecto', payload: p }); }}
        onMouseEnter={() => setActiveIndex(idx)}
        className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-sm text-left transition-colors ${
          active ? 'bg-slate-100' : 'hover:bg-slate-50'
        }`}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.color || '#06b6d4' }} />
        <span className="flex-1 truncate text-slate-800 font-medium">{p.nombre}</span>
        <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 shrink-0">
          {p.slug}
        </span>
      </button>
    );
  };

  const renderTareaRow = (t, idx) => {
    const active = idx === activeIndex;
    const color = t.proyecto?.color || '#06b6d4';
    return (
      <button
        key={`t-${t.id}`}
        data-idx={idx}
        type="button"
        onMouseDown={(e) => { e.preventDefault(); handleSelect({ type: 'tarea', payload: t }); }}
        onMouseEnter={() => setActiveIndex(idx)}
        className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-sm text-left transition-colors ${
          active ? 'bg-slate-100' : 'hover:bg-slate-50'
        }`}
      >
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
        <span className="flex-1 truncate text-slate-800">{t.titulo}</span>
        <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400 shrink-0">
          {t.codigo}
        </span>
      </button>
    );
  };

  // Headers de seccion comparten estilo (igual que los headers de sort en lista).
  const sectionTitle = 'px-3 pt-2 pb-1 text-[10px] font-semibold tracking-widest uppercase text-slate-400';

  return (
    <motion.div
      initial={{ opacity: 0, y: -4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.12, ease: [0.16, 1, 0.3, 1] }}
      ref={listRef}
      className="absolute top-full right-0 mt-1.5 w-[420px] bg-white rounded-lg shadow-xl ring-1 ring-slate-200 z-50 max-h-[60vh] overflow-y-auto py-1"
    >
      {loading && items.length === 0 ? (
        <div className="px-3 py-6 text-xs text-slate-400 text-center">Buscando...</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-6 text-xs text-slate-400 text-center">
          Sin resultados para "{query.trim()}"
        </div>
      ) : (
        <>
          {data.proyectos.length > 0 && (
            <div>
              <div className={sectionTitle}>Proyectos</div>
              {data.proyectos.map((p, i) => renderProyectoRow(p, i))}
            </div>
          )}
          {data.tareas.length > 0 && (
            <div className={data.proyectos.length > 0 ? 'border-t border-slate-100 mt-1 pt-0' : ''}>
              <div className={sectionTitle}>Tareas</div>
              {data.tareas.map((t, i) => renderTareaRow(t, data.proyectos.length + i))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default GlobalSearchDropdown;
